/**
 * `/profile` — learner goal setup + display & accessibility settings
 * (SCH-AI-1b, SCHOOLOGY_STUDENT_AI_PLAN §3.1 + Slice 1b).
 *
 * Two cards over ONE `learning.learner.profile.get` row:
 *   1. "Your goal" — exam kind (vocabulary from `profile.options`, the same
 *      DATA rows `profile.set` validates against), exam date, target score
 *      (% in v1), daily goal minutes, study-day chips (ISO 1 = Mon .. 7 =
 *      Sun; none selected = every day), and the IANA timezone that anchors
 *      ALL day-bucketed math (streaks/goals/digests — §3.1 timezone rule).
 *      Browser timezone is preselected when unset, so the first save
 *      captures it without the learner thinking about it.
 *   2. "Display & accessibility" — dyslexia-friendly type, reduced motion,
 *      low-bandwidth mode (the pinned `display_prefs` keys; classes applied
 *      by `<DisplayPrefsEffect/>` in `lib/display-prefs.tsx`) + reminder
 *      hour. EN/BN locale is a stored slot only (SCH-AI-7).
 *
 * Save semantics: ONE plain (optimistic-free) save button sending ONLY the
 * changed fields — patch semantics end-to-end (`displayPrefs` is a shallow
 * sub-patch; a null value deletes that key server-side). On success the
 * shared profile query is invalidated (display classes re-apply instantly)
 * and a transient "Saved" confirmation renders; server BAD_REQUEST messages
 * surface inline.
 */
import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import { Check, Settings2, Target } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  Label,
  Skeleton,
  cn,
} from '@mangrove/ui';
import { trpc, type AppRouter } from '../lib/trpc.js';
import { RequireAuth } from '../lib/auth.js';
import { LearnShell } from '../components/shell.js';
import { MomentumCard } from '../components/momentum/momentum-card.js';
import { parseDisplayPrefs, type DisplayPrefs } from '../lib/display-prefs.js';

export const Route = createFileRoute('/profile')({
  component: ProfilePage,
});

// ---------------------------------------------------------------------------
// Local types + pure helpers
// ---------------------------------------------------------------------------

type RouterOutputs = inferRouterOutputs<AppRouter>;
type RouterInputs = inferRouterInputs<AppRouter>;
type GoalProfile = NonNullable<RouterOutputs['learning']['learner']['profile']['get']>;
type ProfileSetInput = RouterInputs['learning']['learner']['profile']['set'];

/**
 * Mirrors `DEFAULT_EXAM_KINDS` in `@mangrove/core/learning` (sorted) — the
 * SPA must NOT import `@mangrove/core` (ADR-0058). Used only while
 * `profile.options` is loading/unreachable so the select still works offline;
 * the server list always wins once it arrives.
 */
const FALLBACK_EXAM_KINDS = ['gre', 'ielts', 'pte', 'sat'] as const;

/** ISO weekday chips, 1 = Monday .. 7 = Sunday (§3.1 `study_days`). */
const STUDY_DAYS: ReadonlyArray<{ iso: number; label: string }> = [
  { iso: 1, label: 'Mon' },
  { iso: 2, label: 'Tue' },
  { iso: 3, label: 'Wed' },
  { iso: 4, label: 'Thu' },
  { iso: 5, label: 'Fri' },
  { iso: 6, label: 'Sat' },
  { iso: 7, label: 'Sun' },
];

/** Native-select styling (matches the admin surfaces' `<select>` idiom). */
const SELECT_CLASS =
  'flex min-h-touch w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60';

/** "0 → 12:00 AM, 13 → 1:00 PM" labels for the reminder-hour select. */
function formatHour(hour: number): string {
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}:00 ${hour < 12 ? 'AM' : 'PM'}`;
}

/** The browser's IANA timezone — preselected when the profile has none. */
function browserTimezone(): string {
  try {
    return new Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** Server-side truth, normalized for diffing (null = no profile row yet). */
interface ProfileSnapshot {
  examKind: string | null;
  examDate: string | null;
  targetScore: number | null;
  dailyGoalMinutes: number;
  studyDays: number[];
  timezone: string | null;
  prefs: DisplayPrefs;
}

function snapshotOf(profile: GoalProfile | null): ProfileSnapshot {
  return {
    examKind: profile?.examKind ?? null,
    examDate: profile?.examDate ?? null,
    targetScore: profile?.targetScore ?? null,
    dailyGoalMinutes: profile?.dailyGoalMinutes ?? 20,
    studyDays: [...(profile?.studyDays ?? [])].sort((a, b) => a - b),
    timezone: profile?.timezone ?? null,
    prefs: parseDisplayPrefs(profile?.displayPrefs),
  };
}

/** Editable state — strings for free inputs so in-between values survive. */
interface FormState {
  examKind: string; // '' = not set
  examDate: string; // '' = not set ('YYYY-MM-DD' wire format otherwise)
  targetScore: string; // raw input; '' = not set
  dailyGoalMinutes: string; // raw input
  studyDays: number[];
  timezone: string; // never '' — browser tz preselected when unset
  dyslexiaMode: boolean;
  reducedMotion: boolean;
  lowBandwidth: boolean;
  reminderHour: string; // '' = no reminder, else '0'..'23'
}

function toFormState(snap: ProfileSnapshot): FormState {
  return {
    examKind: snap.examKind ?? '',
    examDate: snap.examDate ?? '',
    targetScore: snap.targetScore != null ? String(snap.targetScore) : '',
    dailyGoalMinutes: String(snap.dailyGoalMinutes),
    studyDays: [...snap.studyDays],
    timezone: snap.timezone ?? browserTimezone(),
    dyslexiaMode: snap.prefs.dyslexiaMode === true,
    reducedMotion: snap.prefs.reducedMotion === true,
    lowBandwidth: snap.prefs.lowBandwidth === true,
    reminderHour: snap.prefs.reminderHour != null ? String(snap.prefs.reminderHour) : '',
  };
}

type BuildPatchResult =
  | { ok: true; patch: ProfileSetInput; hasChanges: boolean }
  | { ok: false; error: string };

/**
 * Diff form vs server snapshot into a `profile.set` patch carrying ONLY the
 * changed fields (§3.1 patch semantics; `displayPrefs` shallow sub-patch —
 * null value = delete key). Client-side bounds mirror the v1 UI contract
 * (target 0–100 %, daily goal 1–1440 min); everything subtler is the
 * server's `validateProfilePatch` and surfaces as an inline BAD_REQUEST.
 */
function buildPatch(form: FormState, snap: ProfileSnapshot): BuildPatchResult {
  const patch: ProfileSetInput = {};

  const examKind = form.examKind === '' ? null : form.examKind;
  if (examKind !== snap.examKind) patch.examKind = examKind;

  const examDate = form.examDate === '' ? null : form.examDate;
  if (examDate !== snap.examDate) patch.examDate = examDate;

  let targetScore: number | null = null;
  if (form.targetScore.trim() !== '') {
    targetScore = Number(form.targetScore);
    if (!Number.isFinite(targetScore) || targetScore < 0 || targetScore > 100) {
      return { ok: false, error: 'Target score must be between 0 and 100%.' };
    }
  }
  if (targetScore !== snap.targetScore) patch.targetScore = targetScore;

  const dailyGoalMinutes = Number(form.dailyGoalMinutes);
  if (!Number.isInteger(dailyGoalMinutes) || dailyGoalMinutes < 1 || dailyGoalMinutes > 1440) {
    return { ok: false, error: 'Daily goal must be a whole number of minutes between 1 and 1440.' };
  }
  if (dailyGoalMinutes !== snap.dailyGoalMinutes) patch.dailyGoalMinutes = dailyGoalMinutes;

  const studyDays = [...form.studyDays].sort((a, b) => a - b);
  if (studyDays.join(',') !== snap.studyDays.join(',')) patch.studyDays = studyDays;

  // Always a concrete IANA name from the select (browser tz when unset), so
  // the FIRST save persists the learner's timezone without an explicit pick.
  if (form.timezone !== (snap.timezone ?? '')) patch.timezone = form.timezone;

  const prefsPatch: Record<string, unknown> = {};
  const boolPrefs = [
    ['dyslexiaMode', form.dyslexiaMode],
    ['reducedMotion', form.reducedMotion],
    ['lowBandwidth', form.lowBandwidth],
  ] as const;
  for (const [key, value] of boolPrefs) {
    const stored = snap.prefs[key] === true;
    // Toggled on → store true; toggled off → null deletes the key (tidy jsonb).
    if (value !== stored) prefsPatch[key] = value ? true : null;
  }
  const reminderHour = form.reminderHour === '' ? null : Number(form.reminderHour);
  const storedHour = snap.prefs.reminderHour ?? null;
  if (reminderHour !== storedHour) prefsPatch['reminderHour'] = reminderHour;
  if (Object.keys(prefsPatch).length > 0) patch.displayPrefs = prefsPatch;

  return { ok: true, patch, hasChanges: Object.keys(patch).length > 0 };
}

// ---------------------------------------------------------------------------
// `profile.options` — additive this slice; typed via a narrow structural cast
// ---------------------------------------------------------------------------

/**
 * `learning.learner.profile.options` ships in this slice, and the generated
 * `@mangrove/api-contract` rollup is only regenerated ONCE at slice end
 * (`gen:contract` — orchestrator-run), so the typed client doesn't know the
 * proc yet. Narrow structural cast over the runtime proxy (which already
 * routes any path): exactly the api's DTO, no `any`. After the regen the
 * cast is redundant but stays valid — safe to delete then.
 * React Query key: `[['learning','learner','profile','options'], { type: 'query' }]`.
 */
interface ProfileOptionsHook {
  useQuery: (input?: undefined) => {
    data: { examKinds: string[] } | undefined;
    isPending: boolean;
    error: { message: string } | null;
  };
}

function useProfileOptions(): ReturnType<ProfileOptionsHook['useQuery']> {
  const profileProcs = trpc.learning.learner.profile as unknown as {
    options: ProfileOptionsHook;
  };
  return profileProcs.options.useQuery();
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

function ProfilePage(): React.JSX.Element {
  return (
    <RequireAuth>
      <ProfileContent />
    </RequireAuth>
  );
}

function ProfileContent(): React.JSX.Element {
  const utils = trpc.useUtils();
  const profile = trpc.learning.learner.profile.get.useQuery();
  const options = useProfileOptions();

  const [form, setForm] = React.useState<FormState | null>(null);
  const [saved, setSaved] = React.useState(false);

  // Hydrate the form ONCE from the first successful load (null row = defaults).
  React.useEffect(() => {
    if (form === null && !profile.isPending && !profile.error) {
      setForm(toFormState(snapshotOf(profile.data ?? null)));
    }
  }, [form, profile.isPending, profile.error, profile.data]);

  const setProfile = trpc.learning.learner.profile.set.useMutation({
    onSuccess: async () => {
      // Shared query — `<DisplayPrefsEffect/>` re-applies classes from this.
      await utils.learning.learner.profile.get.invalidate();
      setSaved(true);
    },
  });

  const update = (patch: Partial<FormState>): void => {
    setSaved(false);
    setForm((f) => (f === null ? f : { ...f, ...patch }));
  };

  const snap = snapshotOf(profile.data ?? null);
  const built = form === null ? null : buildPatch(form, snap);

  const handleSave = (): void => {
    if (setProfile.isPending || built === null || !built.ok || !built.hasChanges) return;
    setProfile.mutate(built.patch);
  };

  // Exam kinds: server vocabulary (fallback mirror while loading/offline),
  // plus the stored value itself so an out-of-vocabulary row still renders.
  const examKinds = React.useMemo<string[]>(() => {
    const kinds = new Set<string>(options.data?.examKinds ?? FALLBACK_EXAM_KINDS);
    if (form?.examKind) kinds.add(form.examKind);
    return [...kinds].sort((a, b) => a.localeCompare(b));
  }, [options.data, form?.examKind]);

  // The runtime's full IANA list; the stored value is prepended if the list
  // omits it (alias names survive round-trips).
  const timezones = React.useMemo<string[]>(() => {
    let zones: string[];
    try {
      zones = Intl.supportedValuesOf('timeZone');
    } catch {
      zones = ['UTC'];
    }
    if (form?.timezone && !zones.includes(form.timezone)) zones = [form.timezone, ...zones];
    return zones;
  }, [form?.timezone]);

  if (form === null) {
    return (
      <LearnShell title="Profile" wide>
        {profile.error ? (
          <EmptyState
            title="Couldn’t load your profile"
            description={profile.error.message}
            action={
              <Button variant="outline" onClick={() => void profile.refetch()}>
                Try again
              </Button>
            }
          />
        ) : (
          <div className="space-y-6">
            <div className="space-y-2">
              <Skeleton className="h-7 w-56" />
              <Skeleton className="h-4 w-80 max-w-full" />
            </div>
            <div className="grid gap-6 md:grid-cols-2 md:items-start">
              <Skeleton className="h-64 w-full rounded-xl" />
              <Skeleton className="h-48 w-full rounded-xl" />
            </div>
          </div>
        )}
      </LearnShell>
    );
  }

  const clientError = built !== null && !built.ok ? built.error : null;
  const serverError = setProfile.error?.message ?? null;
  const hasChanges = built !== null && built.ok && built.hasChanges;
  const pending = setProfile.isPending;

  return (
    <LearnShell title="Profile" wide>
      <div className="ls-stagger space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="space-y-1">
            <h1 className="text-xl font-semibold tracking-tight">Profile &amp; settings</h1>
            <p className="text-sm text-muted-foreground">
              Your study goal and how the app looks and behaves on this account.
            </p>
          </div>
          {saved && !hasChanges ? (
            <span
              data-testid="profile-saved"
              className="inline-flex min-h-touch items-center gap-1.5 rounded-full border border-primary/20 bg-primary/10 px-3 text-sm font-medium text-primary animate-fade-in"
            >
              <Check className="size-4" aria-hidden="true" />
              All changes saved
            </span>
          ) : null}
        </div>

        <div className="grid gap-6 md:grid-cols-2 md:items-start">
          <Card className="card-soft">
            <CardHeader className="space-y-1">
              <CardTitle className="flex items-center gap-3 text-base">
                <span className="flex size-9 items-center justify-center rounded-lg border border-primary/15 bg-primary/10 text-primary">
                  <Target className="size-5" aria-hidden="true" />
                </span>
                Your goal
              </CardTitle>
              <CardDescription>
                What you’re studying toward — it drives your countdown, plan, and progress math.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="profile-exam-kind">Exam</Label>
                  <select
                    id="profile-exam-kind"
                    data-testid="profile-exam-kind"
                    className={SELECT_CLASS}
                    disabled={pending}
                    value={form.examKind}
                    onChange={(e) => update({ examKind: e.target.value })}
                  >
                    <option value="">Choose an exam…</option>
                    {examKinds.map((kind) => (
                      <option key={kind} value={kind}>
                        {kind.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="profile-exam-date">Exam date</Label>
                  <Input
                    id="profile-exam-date"
                    data-testid="profile-exam-date"
                    type="date"
                    className="min-h-touch"
                    disabled={pending}
                    value={form.examDate}
                    onChange={(e) => update({ examDate: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="profile-target-score">Target score (%)</Label>
                  <Input
                    id="profile-target-score"
                    data-testid="profile-target-score"
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={100}
                    step={1}
                    placeholder="e.g. 85"
                    className="min-h-touch"
                    disabled={pending}
                    value={form.targetScore}
                    onChange={(e) => update({ targetScore: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="profile-daily-goal">Daily goal (minutes)</Label>
                  <Input
                    id="profile-daily-goal"
                    data-testid="profile-daily-goal"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={1440}
                    step={5}
                    className="min-h-touch"
                    disabled={pending}
                    value={form.dailyGoalMinutes}
                    onChange={(e) => update({ dailyGoalMinutes: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Study days</Label>
                <div className="flex flex-wrap gap-2" role="group" aria-label="Study days">
                  {STUDY_DAYS.map((day) => {
                    const selected = form.studyDays.includes(day.iso);
                    return (
                      <button
                        key={day.iso}
                        type="button"
                        aria-pressed={selected}
                        data-testid={`study-day-${day.iso}`}
                        disabled={pending}
                        onClick={() =>
                          update({
                            studyDays: selected
                              ? form.studyDays.filter((d) => d !== day.iso)
                              : [...form.studyDays, day.iso],
                          })
                        }
                        className={cn(
                          'press min-h-touch rounded-full border px-4 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                          selected
                            ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                            : 'border-input bg-transparent text-foreground hover:bg-muted/40',
                        )}
                      >
                        {day.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground">
                  No days selected means you plan to study every day.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="profile-timezone">Timezone</Label>
                <select
                  id="profile-timezone"
                  data-testid="profile-timezone"
                  className={SELECT_CLASS}
                  disabled={pending}
                  value={form.timezone}
                  onChange={(e) => update({ timezone: e.target.value })}
                >
                  {timezones.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground">
                  Streaks and daily goals count days in this timezone.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="card-soft">
            <CardHeader className="space-y-1">
              <CardTitle className="flex items-center gap-3 text-base">
                <span className="flex size-9 items-center justify-center rounded-lg border border-primary/15 bg-primary/10 text-primary">
                  <Settings2 className="size-5" aria-hidden="true" />
                </span>
                Display &amp; accessibility
              </CardTitle>
              <CardDescription>How the app looks and behaves on this account.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-1">
              <PrefToggle
                id="pref-dyslexia"
                testId="pref-dyslexia"
                label="Dyslexia-friendly reading"
                description="Larger type with wider letter and line spacing."
                checked={form.dyslexiaMode}
                disabled={pending}
                onChange={(checked) => update({ dyslexiaMode: checked })}
              />
              <PrefToggle
                id="pref-reduced-motion"
                testId="pref-reduced-motion"
                label="Reduce motion"
                description="Minimizes animations and transitions."
                checked={form.reducedMotion}
                disabled={pending}
                onChange={(checked) => update({ reducedMotion: checked })}
              />
              <PrefToggle
                id="pref-low-bandwidth"
                testId="pref-low-bandwidth"
                label="Low-bandwidth mode"
                description="Hides non-essential images to save data."
                checked={form.lowBandwidth}
                disabled={pending}
                onChange={(checked) => update({ lowBandwidth: checked })}
              />

              <div className="space-y-2 border-t pt-4 mt-1">
                <Label htmlFor="profile-reminder-hour">Daily study reminder</Label>
                <select
                  id="profile-reminder-hour"
                  data-testid="profile-reminder-hour"
                  className={SELECT_CLASS}
                  disabled={pending}
                  value={form.reminderHour}
                  onChange={(e) => update({ reminderHour: e.target.value })}
                >
                  <option value="">No reminder</option>
                  {Array.from({ length: 24 }, (_, hour) => (
                    <option key={hour} value={String(hour)}>
                      {formatHour(hour)}
                    </option>
                  ))}
                </select>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* SCH-I3-J — Momentum: streaks + badges. Hidden when the feature is off. */}
        <MomentumCard />

        <div className="flex flex-col items-stretch gap-3 pb-4 sm:flex-row sm:items-center sm:justify-end">
          {clientError ? (
            <p
              role="alert"
              data-testid="profile-error"
              className="text-sm font-medium text-destructive sm:mr-auto"
            >
              {clientError}
            </p>
          ) : serverError ? (
            <p
              role="alert"
              data-testid="profile-error"
              className="text-sm font-medium text-destructive sm:mr-auto"
            >
              {serverError}
            </p>
          ) : null}
          <Button
            data-testid="profile-save"
            className="press min-h-touch w-full sm:w-auto sm:px-8"
            disabled={pending || !hasChanges || clientError !== null}
            onClick={handleSave}
          >
            <Check className="size-4" aria-hidden="true" />
            {pending ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>
    </LearnShell>
  );
}

/** A labeled switch row — `role="switch"` button, 44 px touch target. */
function PrefToggle({
  id,
  testId,
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  id: string;
  testId: string;
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}): React.JSX.Element {
  return (
    <div className="flex items-center justify-between gap-4 py-3 border-t border-border first:border-t-0 first:pt-0">
      <div className="min-w-0 space-y-0.5">
        <Label htmlFor={id}>{label}</Label>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        data-testid={testId}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className="flex min-h-touch shrink-0 cursor-pointer items-center disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span
          aria-hidden="true"
          className={cn(
            'inline-flex h-6 w-11 items-center rounded-full border transition-colors',
            checked ? 'border-primary bg-primary' : 'border-input bg-muted',
          )}
        >
          <span
            className={cn(
              'block size-5 rounded-full bg-background shadow transition-transform',
              checked ? 'translate-x-5' : 'translate-x-0.5',
            )}
          />
        </span>
      </button>
    </div>
  );
}
